import { useMemo } from 'react';
import { Download } from 'lucide-react';

// Import utils
import {
  generateGoogleCalendarUrl,
  generateOutlookCalendarUrl,
  downloadIcsFile,
  type CalendarEvent,
} from '../../utils/calendar';
import { formatTime, indexToHM, hmToViewerMoment } from '../../utils/date';

// Import components
import CloseButton from '../button/CloseButton';

export type ScheduleEventModalProps = {
  isOpen: boolean;
  onClose: () => void;
  eventTitle: string;
  eventBlock: {
    dateKey: string;
    startIndex: number;
    endIndex: number;
  } | null;
  ymdByDateKey: Map<string, string>;
  getYmd: (dateKey: string) => string | undefined;
  TIMES: string[];
  gridStart: string;
  gridEnd: string;
  hourMode: string;
  timezone: string;
  ownerTimezone?: string;
};

const ScheduleEventModal = ({
  isOpen,
  onClose,
  eventTitle,
  eventBlock,
  ymdByDateKey,
  getYmd,
  TIMES,
  gridStart,
  gridEnd,
  hourMode,
  timezone,
  ownerTimezone,
}: ScheduleEventModalProps) => {
  // Prepare calendar event data for export links
  const calendarEvent = useMemo<CalendarEvent | null>(() => {
    if (!eventBlock) return null;

    const ymd =
      getYmd(eventBlock.dateKey) || ymdByDateKey.get(eventBlock.dateKey);
    if (!ymd) return null;

    const baseTz = ownerTimezone || timezone;
    const startHM = indexToHM(gridStart, eventBlock.startIndex);
    const endRow = Math.min(eventBlock.endIndex - 1, TIMES.length - 1);
    const endHM = indexToHM(gridStart, endRow + 1);

    // Convert times to viewer timezone first, then to UTC
    // This ensures the UTC times represent the moment as it appears in the viewer's timezone
    const startViewerMoment = hmToViewerMoment(startHM, ymd, baseTz, timezone);
    const endViewerMoment = hmToViewerMoment(endHM, ymd, baseTz, timezone);

    return {
      title: eventTitle || 'Event',
      start: startViewerMoment.toDate(),
      end: endViewerMoment.toDate(),
      timezone: timezone,
    };
  }, [
    eventBlock,
    ymdByDateKey,
    getYmd,
    TIMES,
    gridStart,
    gridEnd,
    hourMode,
    timezone,
    ownerTimezone,
    eventTitle,
  ]);

  // If modal is not open or no event block, do not render
  if (!isOpen || !eventBlock) return null;

  const ymd = getYmd(eventBlock.dateKey) || ymdByDateKey.get(eventBlock.dateKey) || '';
  const baseTz = ownerTimezone || timezone;
  const startHM = indexToHM(gridStart, eventBlock.startIndex);
  const endRow = Math.min(eventBlock.endIndex - 1, TIMES.length - 1);
  const endHM = indexToHM(gridStart, endRow + 1); // +1 because we want the end of the last cell

  // Convert times to viewer timezone - this ensures date and time match
  const startViewerMoment = hmToViewerMoment(startHM, ymd, baseTz, timezone);
  const endViewerMoment = hmToViewerMoment(endHM, ymd, baseTz, timezone);

  // Get the date label from the actual converted start time (not noon reference)
  // This ensures the date matches the time when timezone conversion crosses midnight
  const dateLabel = startViewerMoment.format('ddd, MMM D, YYYY');
  const startViewer = startViewerMoment.format('HH:mm');
  const endViewer = endViewerMoment.format('HH:mm');
  const startLabel = formatTime(startViewer, hourMode);
  const endLabel = formatTime(endViewer, hourMode);

  const handleGoogleCalendar = () => {
    if (!calendarEvent) return;
    window.open(generateGoogleCalendarUrl(calendarEvent), '_blank');
  };

  const handleOutlookCalendar = () => {
    if (!calendarEvent) return;
    window.open(generateOutlookCalendarUrl(calendarEvent), '_blank');
  };

  const handleDownloadIcs = () => {
    if (!calendarEvent) return;
    downloadIcsFile(calendarEvent);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-2 sm:p-4">
      <div
        className="relative bg-white p-6 w-[420px] max-w-3xl max-h-[calc(100dvh-1rem)] sm:max-h-[90vh] rounded-3xl shadow-xl overflow-y-auto overscroll-y-none hide-scrollbar"
        onClick={(e) => e.stopPropagation()}>
        <CloseButton onClose={onClose} />
        <h2 className="text-xl font-semibold mb-4">Schedule Event</h2>

        <div className="mb-6 space-y-4">
          <div>
            <label className="text-base font-semibold text-gray-700 block mb-1">
              Event
            </label>
            <div className="text-base text-gray-900">
              {eventTitle || 'Untitled Event'}
            </div>
          </div>

          <div>
            <label className="text-base font-semibold text-gray-700 block mb-1">
              Date
            </label>
            <div className="text-base text-gray-900">{dateLabel}</div>
          </div>

          <div>
            <label className="text-base font-semibold text-gray-700 block mb-1">
              Time
            </label>
            <div className="text-base text-gray-900">
              {startLabel} - {endLabel}
            </div>
            <div className="text-sm text-gray-500 mt-1">
              Timezone: {timezone}
            </div>
          </div>
        </div>

        <div className="border-t border-t-gray-200 pt-4">
          <p className="text-base font-semibold text-gray-700 mb-3">
            Add to Calendar
          </p>
          <div className="space-y-2">
            {/* Google Calendar */}
            <button
              onClick={handleGoogleCalendar}
              className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded text-sm font-medium hover:bg-gray-100 transition-colors flex items-center justify-center gap-2">
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              Google Calendar
            </button>

            {/* Outlook Calendar */}
            <button
              onClick={handleOutlookCalendar}
              className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded text-sm font-medium hover:bg-gray-100 transition-colors flex items-center justify-center gap-2">
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M7.5 7.5h9v9h-9v-9z" fill="#0078D4" />
                <path d="M3 3v18h18V3H3zm16 16H5V5h14v14z" fill="#0078D4" />
              </svg>
              Outlook Calendar
            </button>

            {/* ICS File */}
            <button
              onClick={handleDownloadIcs}
              className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded text-sm font-medium hover:bg-gray-100 transition-colors flex items-center justify-center gap-2">
              <Download className="w-5 h-5" />
              Download ICS file
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduleEventModal;
